import React, { useEffect, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { InkSection } from './shared';
import { GET_TINYCMS_FORMS, GET_TINYCMS_PAGES } from '../../graphql';
import { contactNote, type ContactForm, type ContactPage } from './contactNote';
import { showInkToast } from './InkToast';
import { useAddons } from './addons';
import { AskPageSettings } from './AskPageSettings';

/**
 * Content → Design → Reader questions ("tinycms UX optimization review v12",
 * app/TinyCMS App Prototype → Design): reader questions on the public site,
 * answered only from what the site has published (apiserver
 * atomizer/services/readerChat) — as a chat button on every page, an Ask page
 * (/ask, "Ask AI" at the end of the site header), or both.
 *
 * Comes with the Pro plan and needs Atomize; the server decides both on every
 * question, this screen only explains. Account admins switch it on and set it
 * up; any member can try it and see what readers asked. Changes apply to the
 * site within about a minute — no theme publish (unlike the design above it).
 *
 * Suggested questions are written here by the owner and never taken from what
 * readers ask, so nobody can put words on the site by asking.
 */

const READER_CHAT_FIELDS = `
  eligible ineligibleReason enabled mode onMiss contactPath suggestions canManage updatedAt
  forms { formId when }
  insights { asked30d answered30d helpful30d notHelpful30d notCovered { question times reason detail } }
`;

const READER_CHAT = gql`
  query ReaderChat($accountId: ID!) {
    readerChat(accountId: $accountId) { ${READER_CHAT_FIELDS} }
  }
`;

// Drafts from the site's recent topics, for the owner to edit and save.
const READER_CHAT_SUGGESTION_DRAFTS = gql`
  query ReaderChatSuggestionDrafts($accountId: ID!) {
    readerChatSuggestionDrafts(accountId: $accountId)
  }
`;

const UPDATE_READER_CHAT = gql`
  mutation UpdateReaderChat($accountId: ID!, $input: ReaderChatInput!) {
    updateReaderChat(accountId: $accountId, input: $input) { ${READER_CHAT_FIELDS} }
  }
`;

const TRY_READER_CHAT = gql`
  mutation TryReaderChat($accountId: ID!, $question: String!) {
    tryReaderChat(accountId: $accountId, question: $question) {
      status
      answer
      message
      onMiss
      contactPath
      sources {
        n
        title
        path
        section
        date
      }
      related {
        n
        title
        path
        section
        date
      }
    }
  }
`;

interface Source {
  n: number;
  title: string;
  path: string;
  section: string | null;
  date: string | null;
}
interface Reply {
  status: string;
  answer: string | null;
  message: string | null;
  sources: Source[];
  related: Source[];
}
interface Settings {
  eligible: boolean;
  ineligibleReason: string | null;
  enabled: boolean;
  mode: 'chat' | 'page' | 'both';
  onMiss: 'contact' | 'say';
  contactPath: string | null;
  suggestions: string[];
  /** Forms the chat button may fill in with a visitor, a question at a time. */
  forms: Array<{ formId: string; when: string }>;
  canManage: boolean;
  insights: {
    asked30d: number;
    answered30d: number;
    helpful30d: number;
    notHelpful30d: number;
    notCovered: Array<{
      question: string;
      times: number;
      reason?: string | null;
      detail?: string | null;
    }>;
  };
}

const MAX_SUGGESTIONS = 4;
const MAX_CHAT_FORMS = 6;

/** A form as Content → Forms lists it (tinycmsForms). */
type SiteForm = ContactForm & { id: string; state?: { live?: unknown } | null };

const MODES = [
  ['chat', 'Chat button'],
  ['page', 'Ask page'],
  ['both', 'Both'],
] as const;

/** Why a question got no answer, in the owner's words (readerChat/answer.js reasons). */
const missReason = (reason: string | null | undefined) => {
  switch (reason) {
    case 'no_sources':
      return 'Nothing on the site matched the question.';
    case 'model':
      return 'The articles found didn’t clearly answer it.';
    case 'unverified':
      return 'A draft was written, but a check found it said more than the articles do.';
    case null:
    case undefined:
    case '':
      return null;
    default:
      return 'A draft was written, but it failed a format check.';
  }
};

/** "Oct 18", as the site's chat shows a source's date. */
const shortDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null;
};

/** An answer's text with its [n] markers drawn as small numbered chips (text only, never HTML). */
function AnswerText({ text, count }: { text: string; count: number }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of Array.from(text.matchAll(/\[(\d{1,2})\]/g))) {
    const n = Number(m[1]);
    if (n < 1 || n > count) continue;
    if (m.index! > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    parts.push(
      <span key={key++} className="ink-rq-cite">
        {n}
      </span>
    );
    last = m.index! + m[0].length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return <>{parts}</>;
}

export const ReaderQuestions: React.FC<{ accountId: string; siteUrl: string | null }> = ({
  accountId,
  siteUrl,
}) => {
  const { data, loading, error } = useQuery<{ readerChat: Settings }>(READER_CHAT, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const navigate = useNavigate();
  const { atomizeOn } = useAddons();
  const tryRef = useRef<HTMLDivElement>(null);
  const [update, { loading: saving }] = useMutation(UPDATE_READER_CHAT);
  const [tryIt, { loading: asking }] = useMutation<{ tryReaderChat: Reply }>(TRY_READER_CHAT);
  const [loadDrafts, { loading: drafting }] = useLazyQuery<{
    readerChatSuggestionDrafts: string[];
  }>(READER_CHAT_SUGGESTION_DRAFTS, { fetchPolicy: 'network-only' });
  // Drafts from recent topics: the owner's starting point for writing their own.
  // Readers never see these (Prototype v15: with none saved, they see topics).
  const { data: autoData } = useQuery<{ readerChatSuggestionDrafts: string[] }>(
    READER_CHAT_SUGGESTION_DRAFTS,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  const autoSuggestions = autoData?.readerChatSuggestionDrafts || [];
  // "Point to your contact page" needs a page that's there (UX review round 6:
  // the button silently never showed with an empty or deleted address).
  const { data: pageData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const pages: ContactPage[] | null = pageData ? pageData.tinycmsPages || [] : null;
  // Which page shows which form: "Opens About, which has your Contact form".
  const { data: formData } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const forms: SiteForm[] = formData?.tinycmsForms || [];

  const rc = data?.readerChat;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // Forms the chat may fill in: form id → what it's for, in the owner's words.
  const [chatForms, setChatForms] = useState<Record<string, string>>({});
  const [contactPath, setContactPath] = useState('');
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<{ q: string; reply: Reply } | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!rc) return;
    setSuggestions(rc.suggestions);
    setContactPath(rc.contactPath || '');
    setChatForms(Object.fromEntries((rc.forms || []).map((f) => [f.formId, f.when])));
  }, [rc]);

  // Not a tinycms site, or the server predates reader chat: no section.
  if (error && !rc) return null;
  if (loading && !rc) return null;
  if (!rc) return null;

  const canEdit = rc.canManage;
  const save = async (input: Record<string, unknown>, ok?: string) => {
    setMessage(null);
    try {
      await update({
        variables: { accountId, input },
        // The settings have no id for Apollo to merge on: write them back by hand.
        update: (cache, { data: saved }) => {
          if (saved?.updateReaderChat) {
            cache.writeQuery({
              query: READER_CHAT,
              variables: { accountId },
              data: { readerChat: saved.updateReaderChat },
            });
          }
        },
      });
      if (ok) setMessage({ tone: 'ok', text: ok });
    } catch (e: any) {
      setMessage({
        tone: 'error',
        text: e?.graphQLErrors?.[0]?.message || 'Couldn’t save. Please try again.',
      });
    }
  };
  const ask = async (q: string) => {
    const text = q.trim();
    if (!text) return;
    setMessage(null);
    try {
      const res = await tryIt({ variables: { accountId, question: text } });
      if (res.data) setAsked({ q: text, reply: res.data.tryReaderChat });
    } catch (e: any) {
      setMessage({
        tone: 'error',
        text: e?.graphQLErrors?.[0]?.message || 'Couldn’t ask right now.',
      });
    }
  };

  const savedSuggestions = rc.suggestions.join('\n');
  const suggestionsDirty =
    suggestions
      .map((s) => s.trim())
      .filter(Boolean)
      .join('\n') !== savedSuggestions;
  const contactDirty = (contactPath.trim() || null) !== (rc.contactPath || null);
  // In the order Content → Forms lists them.
  const chatFormList = forms
    .filter((f) => chatForms[f.id] !== undefined)
    .map((f) => ({ formId: f.id, when: chatForms[f.id].trim() }));
  const savedFormIds = new Set((rc.forms || []).map((f) => f.formId));
  const formsDirty =
    JSON.stringify(chatFormList) !==
    JSON.stringify(
      forms
        .filter((f) => savedFormIds.has(f.id))
        .map((f) => ({
          formId: f.id,
          when: (rc.forms || []).find((x) => x.formId === f.id)?.when || '',
        }))
    );
  const formsIncomplete = chatFormList.some((f) => !f.when);
  const liveLink = (path: string) => (siteUrl ? `${siteUrl.replace(/\/+$/, '')}${path}` : path);
  const host = siteUrl ? siteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '') : 'your site';
  const modeHelp = {
    chat: 'A chat button in the corner of every page. Good for quick questions while reading.',
    page: `A full page at ${host}/ask, added to the end of your site menu as “Ask”. Answers read like a short article, with cards for the articles used.`,
    both: 'The Ask page in your menu, plus the chat button on every other page.',
  }[rc.mode];
  const onWhere = {
    chat: 'the chat button is',
    page: 'the Ask page is',
    both: 'the Ask page and chat button are',
  }[rc.mode];

  const desc = !rc.eligible
    ? rc.ineligibleReason === 'atomizer'
      ? 'Needs Prism AI, which reads your articles so the chat can answer from them.'
      : 'Comes with the Pro plan.'
    : rc.enabled
      ? `On. Readers see ${onWhere.replace(/ (is|are)$/, '')} within a minute.`
      : 'Off. Readers can’t ask questions on the site.';

  return (
    <InkSection
      id="reader-questions"
      title="Reader questions"
      description="Visitors ask a question, and it answers only from what you’ve published, with links to the articles it used. Offer it as a chat button on every page, as its own Ask page, or both."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28, minWidth: 0 }}>
        {rc.eligible || rc.enabled ? (
          <button
            type="button"
            className="ink-site-ask"
            disabled={!canEdit || saving}
            onClick={() => void save({ enabled: !rc.enabled })}
            aria-pressed={rc.enabled}
          >
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', font: '500 14.5px var(--ink-font)' }}>
                Answer reader questions on the site
              </span>
              <span className="ink-site-row-desc">
                {desc}
                {!canEdit ? ' Only an account admin can change this.' : null}
              </span>
            </span>
            <span className="ink-switch" data-on={rc.enabled} />
          </button>
        ) : (
          // Not on Pro, or Prism AI is off: a row that explains, with the way there.
          <div className="ink-site-ask" style={{ cursor: 'default' }}>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', font: '500 14.5px var(--ink-font)' }}>
                Answer reader questions on the site
              </span>
              <span className="ink-site-row-desc">
                {desc}{' '}
                {rc.ineligibleReason === 'atomizer' ? (
                  <Link to="/prism-ai/research">Turn on Prism AI</Link>
                ) : (
                  <Link to="/settings/billing">See plans</Link>
                )}
              </span>
            </span>
            <span className="ink-switch" data-on={false} aria-hidden="true" />
          </div>
        )}

        {rc.eligible ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Where readers ask</span>
              <span className="ink-seg" style={{ width: 'max-content' }}>
                {MODES.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-active={rc.mode === value}
                    disabled={!canEdit || saving}
                    onClick={() => rc.mode !== value && void save({ mode: value })}
                  >
                    {label}
                  </button>
                ))}
              </span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                {modeHelp}
              </span>
              {rc.enabled && siteUrl ? (
                <span
                  style={{
                    display: 'flex',
                    gap: 16,
                    flexWrap: 'wrap',
                    font: '500 13.5px var(--ink-font)',
                  }}
                >
                  {rc.mode !== 'page' ? (
                    <a href={liveLink('/')} target="_blank" rel="noopener noreferrer">
                      Preview the chat button ↗
                    </a>
                  ) : null}
                  {rc.mode !== 'chat' ? (
                    <a href={liveLink('/ask')} target="_blank" rel="noopener noreferrer">
                      Preview the Ask page ↗
                    </a>
                  ) : null}
                </span>
              ) : null}
            </div>

            {rc.mode !== 'chat' ? (
              <AskPageSettings accountId={accountId} canEdit={canEdit} />
            ) : null}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Suggested questions</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                Shown before a visitor types, and after each reply as next questions (ones they
                haven’t asked yet). Up to {MAX_SUGGESTIONS}, written by you. Nothing readers ask is
                ever shown here on its own.
              </span>
              <div className="ink-site-card" style={{ marginTop: 4, padding: '6px 16px' }}>
                {suggestions.map((s, i) => (
                  <div key={i} className="ink-rq-suggestion">
                    <input
                      className="ink-site-input"
                      value={s}
                      maxLength={120}
                      disabled={!canEdit}
                      placeholder="Suggested question"
                      aria-label={`Suggested question ${i + 1}`}
                      onChange={(e) =>
                        setSuggestions(suggestions.map((x, j) => (j === i ? e.target.value : x)))
                      }
                    />
                    {canEdit ? (
                      <button
                        type="button"
                        className="ink-site-link"
                        onClick={() => setSuggestions(suggestions.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ))}
                {!suggestions.length ? (
                  <div className="ink-site-row-desc" style={{ padding: '10px 0' }}>
                    {/* The nudge (QA round 7): readers get topics to browse, not questions. */}
                    <span style={{ color: 'var(--ink-amber)' }}>
                      No suggested questions yet, so readers see the topics you cover instead. Add 3
                      questions so readers know what to ask.
                    </span>
                    {canEdit && autoSuggestions.length ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="ink-site-link"
                          disabled={drafting}
                          onClick={() =>
                            void loadDrafts({ variables: { accountId } }).then(({ data: d }) => {
                              const drafts = d?.readerChatSuggestionDrafts || [];
                              if (drafts.length) {
                                setSuggestions(drafts);
                                setMessage({
                                  tone: 'ok',
                                  text: 'Edit them, then Save questions. Readers see them once saved.',
                                });
                              } else {
                                setMessage({
                                  tone: 'error',
                                  text: 'No recent topics to draw on. Add a few questions yourself.',
                                });
                              }
                            })
                          }
                        >
                          {drafting ? 'Loading…' : 'Start from your topics'}
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {canEdit ? (
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  {suggestions.length < MAX_SUGGESTIONS ? (
                    <button
                      type="button"
                      className="ink-site-link"
                      onClick={() => setSuggestions([...suggestions, ''])}
                    >
                      Add a question
                    </button>
                  ) : null}
                  {suggestionsDirty ? (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={saving}
                      onClick={() =>
                        void save(
                          { suggestions: suggestions.map((s) => s.trim()).filter(Boolean) },
                          'Suggested questions saved.'
                        )
                      }
                    >
                      Save questions
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Forms the chat can fill in</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                When a visitor asks to do one of these (“I’d like to book an appointment”), the chat
                asks the form’s questions one at a time, reads the answers back, and sends them to
                the form on a yes, just like the form on your page. Responses arrive in Website →
                Forms, marked “via chat”. What visitors type into a form is never sent to Prism AI.
                {rc.mode === 'page'
                  ? ' Forms are filled in through the chat button, so switch on the chat button above to offer them.'
                  : ''}
              </span>
              <div className="ink-site-card" style={{ marginTop: 4, padding: '6px 16px' }}>
                {forms.length ? (
                  forms.map((f) => {
                    const live = Boolean(f.state?.live);
                    const on = chatForms[f.id] !== undefined;
                    const full = !on && Object.keys(chatForms).length >= MAX_CHAT_FORMS;
                    return (
                      <div key={f.id} className="ink-rq-suggestion ink-rq-form">
                        <label className="ink-rq-form-pick">
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={!canEdit || (!live && !on) || full}
                            onChange={(e) => {
                              const next = { ...chatForms };
                              if (e.target.checked) next[f.id] = f.name || '';
                              else delete next[f.id];
                              setChatForms(next);
                            }}
                          />
                          <span>
                            <span style={{ display: 'block', font: '500 13.5px var(--ink-font)' }}>
                              {f.name || 'Untitled form'}
                            </span>
                            {!live ? (
                              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                                Not published yet. Publish it first.
                              </span>
                            ) : null}
                          </span>
                        </label>
                        {on ? (
                          <input
                            className="ink-site-input"
                            value={chatForms[f.id]}
                            maxLength={120}
                            disabled={!canEdit}
                            aria-label={`What ${f.name || 'this form'} is for`}
                            placeholder="e.g. Book an appointment"
                            onChange={(e) => setChatForms({ ...chatForms, [f.id]: e.target.value })}
                          />
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="ink-site-row-desc" style={{ padding: '10px 0' }}>
                    No forms yet. Ask your agent to make one (an appointment request, a contact
                    form) and publish it, then choose it here.
                  </div>
                )}
              </div>
              {canEdit && formsDirty ? (
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="ink-btn ink-btn-primary"
                    disabled={saving || formsIncomplete}
                    onClick={() =>
                      void save(
                        { forms: chatFormList },
                        'Forms saved. The chat offers them within a minute.'
                      )
                    }
                  >
                    Save forms
                  </button>
                  {formsIncomplete ? (
                    <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                      Say what each chosen form is for.
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>
                When the site doesn’t cover a question
              </span>
              <span className="ink-seg" style={{ width: 'max-content' }}>
                {(
                  [
                    ['contact', 'Point to your contact page'],
                    ['say', 'Just say so'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    data-active={rc.onMiss === value}
                    disabled={!canEdit || saving}
                    onClick={() => rc.onMiss !== value && void save({ onMiss: value })}
                  >
                    {label}
                  </button>
                ))}
              </span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                {rc.onMiss === 'contact'
                  ? 'It says it can’t find the answer and offers an “Ask the team” button that opens your contact page.'
                  : 'It says it can’t find the answer and suggests other questions.'}
              </span>
              {rc.onMiss === 'contact' ? (
                <label className="ink-site-label" style={{ maxWidth: 360, marginTop: 6 }}>
                  Contact page address
                  <span style={{ display: 'flex', gap: 10 }}>
                    <input
                      className="ink-site-input"
                      value={contactPath}
                      placeholder="/contact"
                      maxLength={120}
                      disabled={!canEdit}
                      onChange={(e) => setContactPath(e.target.value)}
                    />
                    {canEdit && contactDirty ? (
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        disabled={saving}
                        onClick={() =>
                          void save({ contactPath: contactPath.trim() }, 'Contact page saved.')
                        }
                      >
                        Save
                      </button>
                    ) : null}
                  </span>
                  {(() => {
                    // The address as typed (Prototype v15), so a typo shows before Save.
                    const note = contactNote(contactPath, pages, forms);
                    return (
                      <span
                        className="ink-site-row-desc"
                        style={{
                          marginTop: 0,
                          color:
                            note.tone === 'bad'
                              ? 'var(--ink-red)'
                              : note.tone === 'warn'
                                ? 'var(--ink-amber)'
                                : undefined,
                        }}
                        role={note.tone === 'ok' ? undefined : 'status'}
                      >
                        {note.text}
                      </span>
                    );
                  })()}
                </label>
              ) : null}
            </div>

            <div ref={tryRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>Try it</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                Answers from your live site, the same way readers get them
                {rc.enabled ? '' : ', even while the button is off'}.
              </span>
              <form
                style={{ display: 'flex', gap: 10, maxWidth: 560 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void ask(question);
                }}
              >
                <input
                  className="ink-site-input"
                  value={question}
                  maxLength={300}
                  placeholder="Ask a question a reader might ask"
                  aria-label="Ask a question a reader might ask"
                  onChange={(e) => setQuestion(e.target.value)}
                />
                <button type="submit" className="ink-btn" disabled={asking || !question.trim()}>
                  {asking ? 'Asking…' : 'Ask'}
                </button>
              </form>
              {asked ? (
                <div className="ink-site-card ink-rq-reply">
                  <div className="ink-rq-q">{asked.q}</div>
                  <div className="ink-rq-a">
                    {asked.reply.status === 'answered' && asked.reply.answer ? (
                      <AnswerText text={asked.reply.answer} count={asked.reply.sources.length} />
                    ) : (
                      asked.reply.message
                    )}
                  </div>
                  {/* Sources only for an answer: none under "I couldn't find that" (v14). */}
                  {(asked.reply.status === 'answered' ? asked.reply.sources : []).map((s) => (
                    <a
                      key={`${s.n}-${s.path}`}
                      className="ink-rq-source"
                      href={liveLink(s.path)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="ink-rq-cite">{s.n}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', font: '500 13.5px var(--ink-font)' }}>
                          {s.title}
                        </span>
                        <span className="ink-site-row-desc">
                          {[s.section, shortDate(s.date)].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  ))}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ font: '500 13px var(--ink-font)' }}>What readers asked</span>
              <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                Last 30 days. Only you see these; nothing readers ask is shown on the site.
              </span>
              {/* Four tiles (Prototype v15), not a sentence. */}
              <div className="ink-rq-stats">
                {(
                  [
                    [rc.insights.asked30d, 'Questions'],
                    [
                      rc.insights.answered30d,
                      rc.insights.asked30d
                        ? `Answered · ${Math.round((rc.insights.answered30d / rc.insights.asked30d) * 100)}%`
                        : 'Answered',
                    ],
                    [rc.insights.helpful30d, 'Helpful on the Ask page'],
                    [rc.insights.notHelpful30d, 'Not helpful'],
                  ] as Array<[number, string]>
                ).map(([n, label]) => (
                  <div className="ink-rq-stat" key={label}>
                    <div className="ink-rq-stat-n">{n.toLocaleString()}</div>
                    <div className="ink-rq-stat-label">{label}</div>
                  </div>
                ))}
              </div>
              {rc.insights.notCovered.length ? (
                <span style={{ font: '600 13px var(--ink-font)', marginTop: 8 }}>
                  Not answered: the site doesn’t cover these yet
                </span>
              ) : null}
              {rc.insights.notCovered.length ? (
                <div className="ink-site-card" style={{ marginTop: 4 }}>
                  {rc.insights.notCovered.map((m) => (
                    // Each gap can be checked again or turned into an article (Prototype v14).
                    <div key={m.question} className="ink-site-row ink-rq-miss">
                      <div style={{ minWidth: 0 }}>
                        <div
                          className="ink-site-row-title"
                          style={{ font: '400 14px var(--ink-font)' }}
                        >
                          {m.question}
                        </div>
                        <span className="ink-site-row-desc">
                          {/* Similar wordings count together (readerChat/log.js). */}
                          Asked {m.times === 1 ? 'once' : `${m.times} times`}
                          {missReason(m.reason) ? ` · ${missReason(m.reason)}` : ''}
                        </span>
                        {m.reason === 'unverified' && m.detail ? (
                          <span className="ink-site-row-desc" style={{ display: 'block' }}>
                            The check said: {m.detail}
                          </span>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="ink-link-btn ink-rq-try"
                        disabled={asking}
                        onClick={() => {
                          setQuestion(m.question);
                          tryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          void ask(m.question);
                        }}
                      >
                        Try it
                      </button>
                      {atomizeOn ? (
                        <button
                          type="button"
                          className="ink-link-btn"
                          onClick={() => {
                            navigate(`/prism-ai/write?brief=${encodeURIComponent(m.question)}`);
                            showInkToast(
                              'Opening Write. Your agent drafts from facts you’ve already published.'
                            );
                          }}
                        >
                          Draft an article →
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </>
        ) : null}

        {message ? (
          <div
            className="ink-site-row-desc"
            role="status"
            style={{
              color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)',
              marginTop: 0,
            }}
          >
            {message.text}
          </div>
        ) : null}
      </div>
    </InkSection>
  );
};
